import {ProductState} from '@/product/types';

export const PACK_SIZE = 'Pack Size';

/**
 * Possible product option names that may represent the 'size' of the variant.
 */
export enum SizeOptionName {
  PackSize = 'Pack Size',
  LoadCount = 'Load Count',
}

/**
 * Parses the pack size from a pack option string.
 * This is the number of packs in the option, e.g. '3 Pack' would return 3.
 *
 * @param packOption - The pack option string to parse.
 * @returns The parsed pack size as a number.
 */
export function parsePackSize(packOption: string): number {
  const packSizeMatcher = packOption.match(/\d+/g);
  if (!packSizeMatcher || packSizeMatcher.length === 0) {
    return 1;
  }
  return parseInt(packSizeMatcher[0], 10);
}

/**
 * Checks if the given option name is a size option.
 * This is includes the pack size and load count options.
 * We often treat these options differently in the UI from other options such as scent.
 *
 * @param optionName - The name of the option to check.
 * @returns `true` if the option name is a size option, `false` otherwise.
 */
export function isSizeOption(
  {name}: {name: string},
  sizeOptionNames?: string[] | null
) {
  const names = sizeOptionNames ?? [SizeOptionName.PackSize];
  return names.includes(name);
}

/**
 * Returns the name of the pack option from the given product options.
 * If a size option is found, its name is returned.
 * Otherwise, the default pack size name is returned.
 *
 * @param productOptions - The array of product options.
 * @returns The name of the pack option.
 */
export function getPackOptionName(product: ProductState) {
  return (
    product.options.find((option) =>
      isSizeOption(option, product.sizeOptionNames)
    )?.name ?? SizeOptionName.PackSize
  );
}

/**
 * Checks if the given product has a pack option.
 * This is an option that represents the size of the variant, e.g. '3 Pack'.
 *
 * @param product - the product to check
 * @returns `true` if the product has a pack option, `false` otherwise.
 */
export function hasPackOption(product: ProductState) {
  return product.options.some((option) =>
    isSizeOption(option, product.sizeOptionNames)
  );
}
